import React, { useCallback, useMemo, useState, useRef } from "react";
import _ from "lodash/fp";
import { GridGenerator } from "@actinc/dls/components";
import { Play as PlayIcon } from "@actinc/dls/icons";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  InputAdornment,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useClient } from "urql";

import {
  ControlledFormCheckbox,
  ControlledFormInput,
  ControlledFormSelect,
  DraggableDialog,
} from "../../../components";
import { LoadingButton } from "@mui/lab";

const TTSAccessibilityDialog = ({
  handleClose,
  open,
  option: { elements = [], label, range },
  readonly,
  tag = {},
}) => {
  const audioRef = useRef(null);
  const client = useClient();
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shouldPlay, setShouldPlay] = useState(false);

  const { control, getValues, watch } = useForm({
    mode: "all",
    defaultValues: { ...tag },
  });

  const [anchorEl, setAnchorEl] = useState(null);

  const action = useMemo(
    () => (tag.identifier ? "edit" : "create"),
    [!!tag.identifier],
  );

  const fetchData = useCallback(async () => {
    const text = watch("accessibilityFeatures[0].text");
    if (audioUrl) {
      return;
    }
    setLoading(true);
    try {
      const { data } = await client
        .query(getPresignedPollyURLQuery, { text })
        .toPromise();
      if (data?.getPresignedPollyURL) {
        setAudioUrl(data.getPresignedPollyURL);
        setShouldPlay(true);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  }, [audioUrl, client, watch]);

  const handleConvertText = async (e) => {
    setAnchorEl(e.currentTarget);
    await fetchData();
    setShouldPlay(true);
  };

  return (
    <DraggableDialog
      maxWidth="md"
      open={open}
      title={`${_.capitalize(action)} TTS Accessibility`}
    >
      <DialogContent>
        <form id="tts-dialog-form">
          <GridGenerator
            groups={[
              [
                {
                  children: (
                    <ControlledFormSelect
                      control={control}
                      fullWidth={true}
                      disabled={readonly}
                      id="tts-accessibility-type"
                      label="Accessibility Type"
                      name="accessibilityFeatures[0].type"
                      options={[
                        {
                          disabled: true,
                          label: "Braille",
                          value: "Braille",
                        },
                        {
                          label: "Spoken",
                          value: "Spoken",
                        },
                        {
                          disabled: true,
                          label: "Translation",
                          value: "Translation",
                        },
                      ]}
                      rules={{ required: true }}
                    />
                  ),
                  key: "tts-accessibility-type",
                },
                {
                  children: (
                    <ControlledFormSelect
                      control={control}
                      disabled={readonly}
                      fullWidth={true}
                      id="tts-accessibility-feature"
                      label="Accessibility Feature"
                      name="accessibilityFeatures[0].feature"
                      options={[
                        {
                          disabled: true,
                          label: "Audio Text",
                          value: "Audio Text",
                        },
                        {
                          label: "Text to Speech",
                          value: "Text to Speech",
                        },
                      ]}
                      rules={{ required: true }}
                    />
                  ),
                  key: "tts-accessibility-feature",
                },
              ],
              [
                {
                  children: (
                    <ControlledFormInput
                      control={control}
                      endAdornment={
                        <>
                          <Tooltip
                            aria-label="Listen to text to speech"
                            placement="bottom"
                            title="Listen"
                          >
                            <InputAdornment position="end">
                              <LoadingButton
                                id="playTextToSpeechAudio"
                                loading={!!loading}
                                aria-label="play text to speech audio"
                                edge="end"
                                onClick={handleConvertText}
                                sx={{
                                  "&:hover": { backgroundColor: "transparent" },
                                }}
                              >
                                <PlayIcon />
                              </LoadingButton>
                            </InputAdornment>
                          </Tooltip>
                          {shouldPlay && (
                            <Popover
                              anchorEl={anchorEl}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                              onClose={() => setShouldPlay(false)}
                              id="playTextToSpeechAudio"
                              open={shouldPlay}
                            >
                              <span>
                                <audio ref={audioRef} autoPlay controls>
                                  <source src={audioUrl} type="audio/mpeg" />
                                </audio>
                              </span>
                            </Popover>
                          )}
                        </>
                      }
                      disabled={readonly}
                      fullWidth={true}
                      id="tts-accessible-text"
                      label="Accessible Text"
                      multiline={true}
                      name="accessibilityFeatures[0].text"
                      onChange={() => setAudioUrl("")}
                    />
                  ),
                  key: "tts-accessible-text",
                },
              ],
            ]}
          />
          <Box>
            <Typography>Inclusion Orders</Typography>
            <GridGenerator
              groups={[
                [
                  {
                    children: (
                      <ControlledFormCheckbox
                        control={control}
                        label="Spoken, Text Only : Default"
                        name="inclusionOrders"
                        readonly={true}
                        value="Spoken, Text Only : Default"
                      />
                    ),
                    key: "Spoken, Text Only : Default",
                  },
                  {
                    children: (
                      <ControlledFormCheckbox
                        control={control}
                        label="Spoken, Text Only : On Demand"
                        name="inclusionOrders"
                        readonly={true}
                        value="Spoken, Text Only : On Demand"
                      />
                    ),
                    key: "Spoken, Text Only : On Demand",
                  },
                ],
                [
                  {
                    children: (
                      <ControlledFormCheckbox
                        control={control}
                        label="Spoken, Text and Graphics : Default"
                        name="inclusionOrders"
                        readonly={true}
                        value="Spoken, Text and Graphics : Default"
                      />
                    ),
                    key: "Spoken, Text and Graphics : Default",
                  },
                  {
                    children: (
                      <ControlledFormCheckbox
                        control={control}
                        label="Spoken, Text and Graphics : On Demand"
                        name="inclusionOrders"
                        readonly={true}
                        value="Spoken, Text and Graphics : On Demand"
                      />
                    ),
                    key: "Spoken, Text and Graphics : On Demand",
                  },
                ],
                [
                  {
                    children: (
                      <ControlledFormCheckbox
                        control={control}
                        label="Spoken, Non-visual : Default"
                        name="inclusionOrders"
                        readonly={true}
                        value="Spoken, Non-visual : Default"
                      />
                    ),
                    key: "Spoken, Non-visual : Default",
                  },
                  {
                    children: (
                      <ControlledFormCheckbox
                        control={control}
                        label="Spoken, Graphics Only : On Demand"
                        name="inclusionOrders"
                        readonly={true}
                        value="Spoken, Graphics Only : On Demand"
                      />
                    ),
                    key: "Spoken, Graphics Only : On Demand",
                  },
                ],
              ]}
            />
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()()} variant="text">
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={readonly}
          onClick={() => handleClose({ elements, label, range })(getValues())}
          variant="contained"
        >
          {"Okay"}
        </Button>
      </DialogActions>
    </DraggableDialog>
  );
};

export { TTSAccessibilityDialog };

const getPresignedPollyURLQuery = `
  query GetPresignedPollyURL($text: String!) {
    getPresignedPollyURL(text: $text)
  }
`;
